import type {
  CreatePasteRequest,
  CreatePasteResponse,
  Paste,
  SlugCheckResponse,
  ImageUploadResponse,
  ReactionCounts,
  Comment,
  PasteAnalytics,
  User,
  AdminStats,
  Report,
} from './types';
import { getAuthHeaders, setToken, clearToken } from './auth';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(
  path: string,
  options?: RequestInit,
): Promise<T> {
  const res = await fetch(path, {
    headers: {
      'Content-Type': 'application/json',
      ...(options?.headers ?? {}),
    },
    ...options,
  });

  if (!res.ok) {
    let message = `HTTP ${res.status}`;
    try {
      const body = await res.json() as { error?: string; message?: string };
      message = body.error ?? body.message ?? message;
    } catch {
      // ignore
    }
    throw new ApiError(res.status, message);
  }

  return res.json() as Promise<T>;
}

export async function createPaste(data: CreatePasteRequest): Promise<CreatePasteResponse> {
  return request<CreatePasteResponse>('/api/paste', {
    method: 'POST',
    body: JSON.stringify(data),
    headers: getAuthHeaders(),
  });
}

// ── Auth ──────────────────────────────────────────────────────────────────────

export async function register(
  email: string,
  username: string,
  password: string,
): Promise<{ user: User; token: string }> {
  const result = await request<{ user: User; token: string }>('/api/auth/register', {
    method: 'POST',
    body: JSON.stringify({ email, username, password }),
  });
  setToken(result.token);
  return result;
}

export async function login(
  email: string,
  password: string,
): Promise<{ user: User; token: string }> {
  const result = await request<{ user: User; token: string }>('/api/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
  setToken(result.token);
  return result;
}

export async function getMe(): Promise<{ user: User }> {
  return request<{ user: User }>('/api/auth/me', {
    headers: getAuthHeaders(),
  });
}

export async function getMyPastes(page = 1): Promise<{ pastes: Paste[] }> {
  return request<{ pastes: Paste[] }>(`/api/user/pastes?page=${page}`, {
    headers: getAuthHeaders(),
  });
}

export async function deletePaste(
  pasteId: string,
  creds?: { creatorToken?: string; editCode?: string },
): Promise<void> {
  const headers: Record<string, string> = { ...getAuthHeaders() };
  if (creds?.creatorToken) headers['X-Creator-Token'] = creds.creatorToken;
  if (creds?.editCode) headers['X-Edit-Code'] = creds.editCode;
  await request<unknown>(`/api/paste/${encodeURIComponent(pasteId)}`, {
    method: 'DELETE',
    headers,
  });
}

export { clearToken };

export async function getPaste(idOrSlug: string, password?: string): Promise<Paste> {
  const headers: Record<string, string> = {};
  if (password) {
    headers['X-Paste-Password'] = password;
  }
  return request<Paste>(`/api/paste/${encodeURIComponent(idOrSlug)}`, {
    headers,
  });
}

export async function checkSlug(slug: string): Promise<SlugCheckResponse> {
  return request<SlugCheckResponse>(
    `/api/slug/check?slug=${encodeURIComponent(slug)}`,
  );
}

export async function uploadImage(file: File, sessionId: string): Promise<ImageUploadResponse> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('session_id', sessionId);

  const res = await fetch('/api/upload/image', {
    method: 'POST',
    body: formData,
  });

  if (!res.ok) {
    let message = `HTTP ${res.status}`;
    try {
      const body = await res.json() as { error?: string; message?: string };
      message = body.error ?? body.message ?? message;
    } catch {
      // ignore
    }
    throw new ApiError(res.status, message);
  }

  return res.json() as Promise<ImageUploadResponse>;
}

export async function getReactions(pasteId: string): Promise<{ reactions: ReactionCounts; userReactions: string[] }> {
  return request<{ reactions: ReactionCounts; userReactions: string[] }>(
    `/api/paste/${encodeURIComponent(pasteId)}/reactions`
  );
}

export async function toggleReaction(pasteId: string, emoji: string): Promise<void> {
  await request<unknown>(`/api/paste/${encodeURIComponent(pasteId)}/reactions`, {
    method: 'POST',
    body: JSON.stringify({ emoji }),
  });
}

export async function getComments(pasteId: string, page = 1): Promise<{ comments: Comment[] }> {
  return request<{ comments: Comment[] }>(
    `/api/paste/${encodeURIComponent(pasteId)}/comments?page=${page}`
  );
}

export async function addComment(pasteId: string, content: string, authorName?: string): Promise<Comment> {
  return request<Comment>(`/api/paste/${encodeURIComponent(pasteId)}/comments`, {
    method: 'POST',
    body: JSON.stringify({ content, author_name: authorName }),
  });
}

export async function getAnalytics(pasteId: string): Promise<PasteAnalytics> {
  return request<PasteAnalytics>(`/api/paste/${encodeURIComponent(pasteId)}/analytics`);
}

export interface PasteOptions {
  title: string;
  slug: string;
  visibility: 'public' | 'unlisted' | 'password';
  password: string;
  tags: string[];
  expiresIn: number | null;
  accent_color: string;
  header_image: string;
  custom_font: string;
  edit_code: string;
  searchable: boolean;
}

export function defaultPasteOptions(): PasteOptions {
  return {
    title: '',
    slug: '',
    visibility: 'public',
    password: '',
    tags: [],
    expiresIn: null,
    accent_color: '',
    header_image: '',
    custom_font: 'default',
    edit_code: '',
    searchable: false,
  };
}

// ── Admin ─────────────────────────────────────────────────────────────────────

export async function getAdminStats(): Promise<AdminStats> {
  return request<AdminStats>('/api/admin/stats', {
    headers: getAuthHeaders(),
  });
}

export async function getAdminPastes(page = 1): Promise<{ pastes: Paste[]; total: number }> {
  return request<{ pastes: Paste[]; total: number }>(`/api/admin/pastes?page=${page}`, {
    headers: getAuthHeaders(),
  });
}

export async function adminDeletePaste(id: string): Promise<void> {
  await request<unknown>(`/api/admin/pastes/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
}

export async function getAdminUsers(page = 1): Promise<{ users: User[]; total: number }> {
  return request<{ users: User[]; total: number }>(`/api/admin/users?page=${page}`, {
    headers: getAuthHeaders(),
  });
}

export async function toggleAdmin(userId: string): Promise<void> {
  await request<unknown>(`/api/admin/users/${encodeURIComponent(userId)}/toggle-admin`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });
}

export async function reportPaste(pasteId: string, reason: string): Promise<void> {
  await request<unknown>(`/api/paste/${encodeURIComponent(pasteId)}/report`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

export async function getAdminReports(status = 'pending'): Promise<{ reports: Report[] }> {
  return request<{ reports: Report[] }>(`/api/admin/reports?status=${encodeURIComponent(status)}`, {
    headers: getAuthHeaders(),
  });
}

export async function reviewReport(reportId: string, action: 'dismiss' | 'delete_paste'): Promise<void> {
  await request<unknown>(`/api/admin/reports/${encodeURIComponent(reportId)}/review`, {
    method: 'POST',
    body: JSON.stringify({ action }),
    headers: getAuthHeaders(),
  });
}

// ── Edit / Versions ───────────────────────────────────────────────────────────

export async function updatePaste(
  id: string,
  data: Partial<CreatePasteRequest>,
  creatorToken?: string,
  editCode?: string,
): Promise<Paste> {
  const headers: Record<string, string> = { ...getAuthHeaders() };
  if (creatorToken) headers['X-Creator-Token'] = creatorToken;
  if (editCode) headers['X-Edit-Code'] = editCode;
  return request<Paste>(`/api/paste/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify(data),
    headers,
  });
}

export async function getPasteVersions(
  id: string,
  password?: string,
): Promise<{ versions: Array<{ version: number; title: string | null; created_at: number }> }> {
  const headers: Record<string, string> = { ...getAuthHeaders() };
  if (password) headers['X-Paste-Password'] = password;
  return request<{ versions: Array<{ version: number; title: string | null; created_at: number }> }>(
    `/api/paste/${encodeURIComponent(id)}/versions`,
    { headers },
  );
}

export async function getPasteVersion(
  id: string,
  version: number,
  password?: string,
): Promise<{ content: string; title: string | null; version: number; created_at: number }> {
  const headers: Record<string, string> = { ...getAuthHeaders() };
  if (password) headers['X-Paste-Password'] = password;
  return request<{ content: string; title: string | null; version: number; created_at: number }>(
    `/api/paste/${encodeURIComponent(id)}/versions/${version}`,
    { headers },
  );
}

export async function claimPaste(
  pasteId: string,
  creds: { creatorToken?: string; editCode?: string },
): Promise<void> {
  const headers: Record<string, string> = { ...getAuthHeaders() };
  if (creds.creatorToken) headers['X-Creator-Token'] = creds.creatorToken;
  if (creds.editCode) headers['X-Edit-Code'] = creds.editCode;
  await request<unknown>(`/api/paste/${encodeURIComponent(pasteId)}/claim`, {
    method: 'POST',
    headers,
  });
}

// ── Collections ───────────────────────────────────────────────────────────────

export async function getCollections(): Promise<{ collections: any[] }> {
  return request<{ collections: any[] }>('/api/collections', {
    headers: getAuthHeaders(),
  });
}

export async function createCollection(name: string, description?: string, isPublic = false): Promise<any> {
  return request<any>('/api/collections', {
    method: 'POST',
    body: JSON.stringify({ name, description, is_public: isPublic }),
    headers: getAuthHeaders(),
  });
}

export async function getCollection(id: string): Promise<any> {
  return request<any>(`/api/collections/${encodeURIComponent(id)}`, {
    headers: getAuthHeaders(),
  });
}

export async function addToCollection(collectionId: string, pasteId: string): Promise<void> {
  await request<unknown>(`/api/collections/${encodeURIComponent(collectionId)}/pastes`, {
    method: 'POST',
    body: JSON.stringify({ paste_id: pasteId }),
    headers: getAuthHeaders(),
  });
}

export async function removeFromCollection(collectionId: string, pasteId: string): Promise<void> {
  await request<unknown>(
    `/api/collections/${encodeURIComponent(collectionId)}/pastes/${encodeURIComponent(pasteId)}`,
    { method: 'DELETE', headers: getAuthHeaders() },
  );
}

// ── Admin Live Stats ──────────────────────────────────────────────────────────

export async function getAdminLiveStats(): Promise<{
  pastes_last_hour: number;
  views_today: number;
  users_last_hour: number;
  as_of: number;
}> {
  return request<{
    pastes_last_hour: number;
    views_today: number;
    users_last_hour: number;
    as_of: number;
  }>('/api/admin/stats/live', {
    headers: getAuthHeaders(),
  });
}

